<template>
  <BaseType
    v-if="db"
    class="default-type-chip"
    state="box"
    :image="icon"
    :color="color"
    :title="db.data_name"
    @click="openDatabase"
  />
</template>
<script lang="ts">
import { computed, defineComponent, PropType } from "vue";

import type { TableDefaultExtensionsKind } from "@ctrl/controller-rpc";

import BaseType from "@/components/base/BaseType.vue";
import { getTypeColor, getTypeIcon } from "@/components/databases";
import * as inject from "@/components/inject";
import { getDefaultDatabase } from "@/composables/useProfile";
import { navigateToDatabase } from "@/utils/navigate";

/**
 * The type chip shown above the title of the built-in object pages (Task,
 * Event, Page, Person) — the counterpart of the database chip on custom
 * object pages (see the `.type` row in graph/Object.vue). Renders the
 * built-in kind's database as a `BaseType` box and opens its database view
 * on click.
 */
export default defineComponent({
  name: "DefaultTypeChip",
  components: { BaseType },
  props: {
    kind: {
      type: String as PropType<TableDefaultExtensionsKind>,
      required: true,
    },
  },
  setup(props) {
    const profile = inject.profile();
    const router = inject.router();

    // Live off the profile's per-kind schema subscription.
    const db = computed(() => getDefaultDatabase(profile.value, props.kind));

    const icon = computed(() => getTypeIcon(profile.value, db.value));
    const color = computed(() => getTypeColor(db.value));
    const openDatabase = async () => {
      if (!db.value) return;
      await navigateToDatabase(
        router,
        profile.value.user.personal.workspaceId,
        db.value.id
      );
    };

    return { color, db, icon, openDatabase };
  },
});
</script>
<style lang="scss" scoped>
.default-type-chip {
  // The box chip carries 6px horizontal padding; pull it left so its label
  // left-aligns with the title text below (like `.base-type` in Object.vue).
  margin-inline-start: -6px;
}
</style>
